import { z } from 'zod';
import { QualityScenarioSchema, SCENARIO_STATUS, ScenarioReviewSchema } from './scenarios';

/**
 * Logical lifecycle names mapped onto existing SCENARIO_STATUS (+ review.state).
 *
 * DRAFT              → draft
 * IN_REVIEW          → review (+ review.state pending)
 * CHANGES_REQUESTED  → draft + review.state changes_requested (then re-submit)
 * APPROVED           → approved
 * READY              → approved (UI "Ready" / activate hop) — no separate wire status
 * ACTIVE             → active
 * ARCHIVED           → archived
 *
 * needs_review / obsolete remain for failure-intelligence / deprecation.
 */

export const SCENARIO_LIFECYCLE_ACTIONS = [
  'submit_review',
  'approve',
  'request_changes',
  'mark_ready',
  'reopen_review',
  'archive',
  'return_to_draft',
] as const;
export type ScenarioLifecycleAction = (typeof SCENARIO_LIFECYCLE_ACTIONS)[number];

export const ScenarioLifecycleTransitionSchema = z.object({
  from: z.enum(SCENARIO_STATUS),
  to: z.enum(SCENARIO_STATUS),
  action: z.enum(SCENARIO_LIFECYCLE_ACTIONS),
  actorUserId: z.string().uuid().nullable().optional(),
  actorName: z.string().optional(),
  comment: z.string().optional(),
  timestamp: z.string(),
});
export type ScenarioLifecycleTransition = z.infer<typeof ScenarioLifecycleTransitionSchema>;

export const ScenarioLifecycleValidationIssueSchema = z.object({
  code: z.string(),
  message: z.string(),
  path: z.string().optional(),
});
export type ScenarioLifecycleValidationIssue = z.infer<typeof ScenarioLifecycleValidationIssueSchema>;

export const ScenarioLifecycleCanTransitionSchema = z.object({
  allowed: z.boolean(),
  reason: z.string().optional(),
  validationErrors: z.array(ScenarioLifecycleValidationIssueSchema).default([]),
});
export type ScenarioLifecycleCanTransition = z.infer<typeof ScenarioLifecycleCanTransitionSchema>;

export const SCENARIO_LIFECYCLE_HISTORY_ACTIONS = [
  'SUBMITTED',
  'APPROVED',
  'CHANGES_REQUESTED',
  'REOPENED',
  'ACTIVATED',
  'ARCHIVED',
  'RETURNED_TO_DRAFT',
] as const;
export type ScenarioLifecycleHistoryAction = (typeof SCENARIO_LIFECYCLE_HISTORY_ACTIONS)[number];

export const ScenarioLifecycleHistoryItemSchema = z.object({
  /** Synthetic id (`{reviewId}-submit` / `-approve` / `-changes`) — not a DB PK. */
  id: z.string().min(1),
  scenarioId: z.string().uuid(),
  action: z.enum(SCENARIO_LIFECYCLE_HISTORY_ACTIONS),
  authorUserId: z.string().uuid().nullable(),
  authorName: z.string(),
  comment: z.string().default(''),
  fromStatus: z.enum(SCENARIO_STATUS).nullable().optional(),
  toStatus: z.enum(SCENARIO_STATUS).nullable().optional(),
  timestamp: z.string(),
  review: ScenarioReviewSchema.nullable().optional(),
});
export type ScenarioLifecycleHistoryItem = z.infer<typeof ScenarioLifecycleHistoryItemSchema>;

export const ScenarioLifecycleHistoryResponseSchema = z.object({
  scenarioId: z.string().uuid(),
  items: z.array(ScenarioLifecycleHistoryItemSchema),
});
export type ScenarioLifecycleHistoryResponse = z.infer<typeof ScenarioLifecycleHistoryResponseSchema>;

export const ScenarioLifecycleActionResponseSchema = z.object({
  scenario: QualityScenarioSchema,
  transition: ScenarioLifecycleTransitionSchema,
  review: ScenarioReviewSchema.nullable().optional(),
  validationErrors: z.array(ScenarioLifecycleValidationIssueSchema).optional(),
});
export type ScenarioLifecycleActionResponse = z.infer<typeof ScenarioLifecycleActionResponseSchema>;

export const RequestChangesBodySchema = z.object({
  comment: z.string().trim().min(1).max(2000),
});
export type RequestChangesBody = z.infer<typeof RequestChangesBodySchema>;

export const ApproveScenarioBodySchema = z.object({
  comment: z.string().max(2000).optional(),
});
export type ApproveScenarioBody = z.infer<typeof ApproveScenarioBodySchema>;
